<template>
  <v-container v-if="getPermission('read')" fluid class="b-container">
    <v-sheet class="pa-3" v-if="headers.length">
      <v-row no-gutters align="center" justify="space-between" class="pa-4">
        <v-col cols="auto">
          <v-img src="@/assets/transactions.png" width="60" height="60"></v-img>
        </v-col>

        <v-col class="pl-3">
          <h4 v-text="translations.title[lang]"></h4>
          <div
            style="font-size: 0.875rem"
            class="font-weight-medium"
            v-text="translations.info[lang]"
          ></div>
        </v-col>

        <v-col cols="3" class="pl-3" v-if="getPermission('download_transactions')">
          <div v-if="!lastZUrl" class="text-right">
            <v-tooltip color="secondary" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="secondary"
                  class="text-capitalize pr-6"
                  depressed
                  v-on="on"
                  :loading="lastZLoading"
                  :disabled="lastZLoading"
                  @click="getLastZ()"
                >
                  {{ translations.last_z[lang] }}
                </v-btn>
              </template>

              <span class="font-weight-bold">{{ translations.fundClosureHover[lang] }}</span>
            </v-tooltip>
          </div>
          <div v-else class="text-right">
            <v-tooltip color="secondary" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="success"
                  class="text-capitalize pr-6"
                  depressed
                  v-on="on"
                  @click="clearLastZUrl(lastZUrl)"
                >
                  {{ translations.last_z_download[lang] }}
                </v-btn>
              </template>

              <span class="font-weight-bold">{{ translations.fundClosureHover[lang] }}</span>
            </v-tooltip>
          </div>
        </v-col>

        <v-col cols="4" class="pl-3" v-if="last24Revenue.length > 0">
          <div class="daily-revenue-box">
            <v-row class="pl-6" v-for="(item, i) in last24Revenue" :key="i">
              <v-col cols="8">
                <v-row>
                  <h5>
                    {{ translations.dailyRevenueTitle[lang] + item.bank_provider_name }}
                  </h5>
                </v-row>
                <v-row>
                  <span class="font-weight-bold caption">
                    {{ translations.dailyRevenueDescription[lang] }}
                  </span>
                </v-row>
              </v-col>
              <v-col cols="4" v-if="item.total_price">
                <div class="price-box">
                  <v-icon v-text="icons.mdiCurrencyEur" width="10"></v-icon>
                  {{ item.total_price }}
                </div>
              </v-col>
              <v-col cols="4" v-else>
                <div class="price-box red--text font-italic">No data</div>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <ExportLinks
          v-if="getPermission('download_transactions')"
          export-key="transactions"
          :filters="exportFilters"
        />

        <v-col
          cols="3"
          class="ml-auto pt-0 pb-0 pr-1 d-flex justify-end align-center"
          style="font-size: 0.875rem"
        >
          <span class="pa-1">{{ translations.orders[lang] }}</span>

          <v-tooltip color="secondary" top max-width="300">
            <template v-slot:activator="{ on }">
              <v-icon
                class="b-social-icon"
                style="cursor: pointer"
                size="18"
                v-on="on"
                color="gray"
                v-text="icons.mdiInformationVariantCircle"
                @click="openInfoDialog(item[0])"
              ></v-icon>
            </template>

            <span class="font-weight-bold" v-text="ordersMethod.description[lang]"></span>
          </v-tooltip>
        </v-col>
        <v-col cols="1" class="d-flex justify-left align-center">
          <v-sheet width="40" class="">
            <v-skeleton-loader
              v-if="storeOrdersFlag == undefined"
              type="button"
              height="20"
            ></v-skeleton-loader>
            <v-switch
              v-else
              v-model="ordersAllowedOption"
              :loading="loading"
              color="secondary"
              class="mt-0 pt-0"
              hide-details
              @change="ordersAllowedChanged()"
            ></v-switch>
          </v-sheet>
        </v-col>
      </v-row>
      <Filters />
      <b-data-table
        :loading="loading"
        :headers="headers"
        :items="transactions"
        :page.sync="page"
        @page-sync="(p) => (page = p)"
        disable-sort
        class="b-outlined"
      >
        <template v-slot:no-data>
          <span v-if="!loading" v-text="translations.noData[lang]"></span>
        </template>

        <template v-slot:item.transaction_id="{ item }"> # {{ item.transaction_id }} </template>

        <template v-slot:item.email="{ item }">
          {{ item.email || '-' }}
        </template>

        <template v-slot:item.total_price="{ item }">
          {{
            new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'EUR',
              minimumFractionDigits: 2,
            }).format(item.total_price)
          }}
        </template>

        <template v-slot:item.total_amount="{ item }">
          {{
            new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'EUR',
              minimumFractionDigits: 2,
            }).format(item.total_amount)
          }}
        </template>

        <template v-slot:item.delivery_fee="{ item }">
          {{
            new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'EUR',
              minimumFractionDigits: 2,
            }).format(item.delivery_fee)
          }}
        </template>

        <template v-slot:item.delivery_address="{ item }">
          <div
            v-if="item.delivery_address"
            class="text-decoration-underline"
            style="cursor: pointer"
            @click="
              () => {
                deliveryDetails = item.delivery_address;
                deliveryDialog = true;
              }
            "
          >
            <v-icon color="secondary" v-text="icons.mdiMapMarker"></v-icon>
            {{ translations.deliveryDetails[lang] }}
          </div>

          <span v-else>
            <v-icon color="secondary" v-text="icons.mdiMapMarker"></v-icon>
            {{ translations.pickupFromStore[lang] }}
          </span>
        </template>

        <template v-slot:item.show_details="{ item }">
          <div class="d-flex flex-row">
            <v-tooltip color="secondary" bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  color="secondary"
                  v-on="on"
                  @click="
                    () => {
                      transactionId = item.transaction_id;
                      transactionDialog = true;
                    }
                  "
                >
                  <v-icon v-text="icons.mdiTextBoxOutline"></v-icon>
                </v-btn>
              </template>

              <span class="font-weight-bold" style="white-space: pre">{{
                translations.transactionDetails[lang]
              }}</span>
            </v-tooltip>

            <v-tooltip v-if="getPermission('view.read')" color="secondary" bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  color="secondary"
                  v-on="on"
                  @click="openCustomerDialog(item.user_account_id, item.user_identity)"
                >
                  <v-icon v-text="icons.mdiAccountDetails"></v-icon>
                </v-btn>
              </template>

              <span class="font-weight-bold" style="white-space: pre">{{
                translations.customerDetails[lang]
              }}</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.clearer="{ item }">
          <div>
            {{
              item.transaction_status_id === 8
                ? '-'
                : item.clearer
                  ? item.clearer
                  : translations.cash[lang]
            }}
          </div>
        </template>

        <template v-slot:item.transaction_status_name="{ item }">
          <v-alert
            v-if="item.transaction_status_id === 3"
            type="success"
            :color="item.refunded ? 'blue' : ''"
            dense
            class="mb-0 text-center"
          >
            <span class="mr-6 subtitle-2">{{
              item.refunded ? translations.refunded[lang] : translations.verified[lang]
            }}</span>
          </v-alert>
          <v-sheet v-else>
            <!-- <b-select
                            v-model="item.transaction_status_id"
                            :items="transactionStatuses"
                            :loading="item.loading"
                            item-text="name"
                            item-value="transaction_status_id"
                            class="mb-3"
                            @change="changeStatus(item)"
                            disabled
                        >
                            <template v-slot:prepend-inner>
                                <div
                                    v-if="item.transaction_status_id === 1"
                                    class="pt-1 mr-1"
                                >
                                    <img src="@/assets/clock.png" width="20" />
                                </div>
                                <div
                                    v-else-if="item.transaction_status_id === 2"
                                    class="pt-1 mr-1"
                                >
                                    <img src="@/assets/flag.png" width="20" />
                                </div>
                            </template>
                        </b-select> -->
            <img src="@/assets/clock.png" width="20" v-if="item.transaction_status_id === 1" />
            <img src="@/assets/flag.png" width="20" v-if="item.transaction_status_id === 2" />
            {{ item.transaction_status_name }}
          </v-sheet>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="item.transaction_status_id === 2"
            color="grey lighten-2"
            class="text-capitalize my-1 red--text"
            width="83"
            depressed
            @click="
              () => {
                transactionId = item.transaction_id;
                dialog = true;
              }
            "
            >{{ translations.refund[lang] }}</v-btn
          >
          <v-btn
            v-else-if="isSuperAdmin"
            color="grey lighten-2"
            class="text-capitalize my-1 red--text"
            width="83"
            depressed
            @click="
              () => {
                transactionId = item.transaction_id;
                cancelDialog = true;
              }
            "
            >{{ translations.cancel[lang] }}</v-btn
          >
          <v-btn
            v-if="item.transaction_status_id === 2"
            color="grey lighten-2"
            class="text-capitalize my-1 green--text"
            width="83"
            depressed
            @click="
              () => {
                item.transaction_status_id = 3;
                changeStatus(item);
              }
            "
            >{{ translations.verify[lang] }}</v-btn
          >
        </template>
      </b-data-table>

      <v-dialog v-model="settingsDialog" max-width="500">
        <SettingsPanel @cancel="settingsDialog = false" />
      </v-dialog>

      <v-dialog v-model="deliveryDialog" max-width="500">
        <DeliveryDetails :delivery-details="deliveryDetails" @cancel="deliveryDialog = false" />
      </v-dialog>

      <v-dialog v-model="transactionDialog" max-width="600">
        <TransactionProfile :transaction-id="transactionId" @cancel="transactionDialog = false" />
      </v-dialog>

      <v-dialog v-model="dialog" max-width="600">
        <RefundTransaction :transaction-id="transactionId" @cancel="dialog = false" />
      </v-dialog>
      <v-dialog v-model="cancelDialog" max-width="600">
        <CancelTransaction :transaction-id="transactionId" @cancel="cancelDialog = false" />
      </v-dialog>
      <v-dialog v-model="superUserConfirmationDialog" max-width="500">
        <b-card
          title="Cancel Transaction"
          :submit-text="{ el: 'Yes', en: 'Yes', it: 'Yes' }"
          :cancel-text="{ el: 'No', en: 'No', it: 'No' }"
          @cancel="superUserConfirmationDialog = false"
          @submit="
            cancelTransaction({
              id: transactionId,
              token: confirmationToken,
              query,
            })
          "
        >
          <div class="subtitle-1 font-weight-medium pl-2">
            The user has a claimed goal coupon. If you cancel the transaction then user will lose
            the goal coupon. Are you sure you want to proceed with the cancellation of the
            transaction?
          </div>
        </b-card>
      </v-dialog>

      <v-dialog v-model="customerDialog" max-width="700">
        <CustomerForm
          :customerData="customerData"
          :userAccountId="userAccountId"
          :isCustomer="true"
          @close="customerDialog = false"
        />
      </v-dialog>
    </v-sheet>
  </v-container>
</template>

<script>
import transactions from '@/store/modules/storePanel/transactions';

import {
  mdiCheckBold,
  mdiMapMarker,
  mdiCurrencyEur,
  mdiTextBoxOutline,
  mdiAccountDetails,
  mdiInformationVariantCircle,
} from '@mdi/js';
import { mapState, mapMutations, mapActions } from 'vuex';

import Filters from '@/components/storePanel/transactions/Filters.vue';
import SettingsPanel from '@/components/storePanel/transactions/SettingsPanel.vue';
import DeliveryDetails from '@/components/storePanel/transactions/DeliveryDetails.vue';
import TransactionProfile from '@/components/storePanel/transactions/TransactionProfile.vue';
import RefundTransaction from '@/components/storePanel/transactions/RefundTransaction.vue';
import CancelTransaction from '@/components/storePanel/transactions/CancelTransaction.vue';
import CustomerForm from '@/components/storePanel/transactions/CustomerForm.vue';
import translations from '@/utils/translations/storePanel/transactions';
import ExportLinks from '@/components/general/ExportLinks.vue';
import rewardMethods from '@/utils/storePanel/rewardMethods';

export default {
  name: 'Transactions',

  components: {
    Filters,
    SettingsPanel,
    DeliveryDetails,
    TransactionProfile,
    RefundTransaction,
    CancelTransaction,
    ExportLinks,
    CustomerForm,
  },

  mixins: [translations, rewardMethods],

  data() {
    return {
      icons: {
        mdiCheckBold,
        mdiMapMarker,
        mdiCurrencyEur,
        mdiTextBoxOutline,
        mdiAccountDetails,
        mdiInformationVariantCircle,
      },
      page: +this.$route.query.page,
      settingsDialog: false,
      deliveryDialog: false,
      deliveryDetails: {},
      transactionDialog: false,
      transactionId: null,
      userAccountId: null,
      customerDialog: false,
      ordersAllowedOption: false,
    };
  },

  computed: {
    ...mapState([
      'loading',
      'errorMessage',
      'serverItemsLength',
      'superUser',
      'isSuperAdmin',
      'notificationsTrigger',
      'permissions',
      'storePermissions',
    ]),
    ...mapState('storePanel', ['store']),
    ...mapState('storePanel/transactions', [
      'transactionStatuses',
      'transactions',
      'last24Revenue',
      'lastZUrl',
      'lastZLoading',
      'confirmationToken',
      'customerData',
      'ordersAllowed',
    ]),

    lang() {
      return this.$route.params.lang;
    },

    // permission() {
    //     return this.$store.state.permissions.transactions
    //         ? this.$store.state.permissions.transactions.read
    //         : null;
    // },

    ordersMethod() {
      return this.rewardMethods.find((x) => x.updateProp == 'orders_allowed');
    },

    headers() {
      let headers = [
        {
          text: 'Id',
          value: 'transaction_id',
          width: 30,
          permission: 'id',
        },
        {
          text: this.translations.user[this.lang],
          value: 'user_identity',
          permission: 'user',
        },
        {
          text: 'Email',
          value: 'email',
          permission: 'email',
        },
        {
          text: this.translations.points[this.lang],
          value: 'total_points',
          permission: 'points',
        },
        {
          text: this.translations.totalAmount[this.lang],
          value: 'total_amount',
          permission: 'total_amount',
        },
        {
          text: this.translations.deliveryFee[this.lang],
          value: 'delivery_fee',
          permission: 'delivery_fee',
        },
        {
          text: this.translations.delivery[this.lang],
          value: 'delivery_address',
          width: 120,
          permission: 'delivery',
        },
        {
          text: this.translations.date[this.lang],
          value: 'created_at',
          permission: 'date',
        },
        {
          text: this.translations.showDetails[this.lang],
          value: 'show_details',
          width: 70,
          permission: 'actions',
        },
        {
          text: this.translations.clearer[this.lang],
          value: 'clearer',
          width: 100,
          permission: 'bank',
        },
        {
          text: this.translations.status[this.lang],
          value: 'transaction_status_name',
          width: 190,
          permission: 'status',
        },
        {
          text: this.translations.actions[this.lang],
          value: 'actions',
          permission: 'actions',
        },
      ];
      return headers.filter((header) => this.getPermission(`${header.permission}.read`));
    },

    transactionsLength() {
      return this.$store.state.storePanel.transactions.transactions.length;
    },

    dialog: {
      get() {
        return this.$store.state.dialog;
      },

      set(val) {
        this.setDialog(val);
      },
    },

    cancelDialog: {
      get() {
        return this.$store.state.storePanel.transactions.cancelDialog;
      },

      set(val) {
        this.setCancelDialog(val);
      },
    },

    superUserConfirmationDialog: {
      get() {
        return this.$store.state.storePanel.transactions.superUserConfirmationDialog;
      },

      set(val) {
        this.setSuperUserConfirmationDialog(val);
      },
    },

    transaction: {
      get() {
        return this.$store.state.storePanel.transactions.transaction;
      },

      set(val) {
        this.setItem(val);
      },
    },

    query() {
      let query = '?';

      for (let key in this.$route.query) {
        if (this.$route.query[key]) query += `${key}=${this.$route.query[key]}&`;
      }

      return query.slice(0, query.length - 1);
    },

    exportFilters() {
      let filters = { ...this.$route.query };
      delete filters.page;
      return filters;
    },
    storeOrdersFlag() {
      return this.store.flags?.reward?.orders_allowed;
    },
  },

  mounted() {
    // this.getItems(this.query);
    this.getTransactionsStatus();
    this.getLast24Revenue();
    this.getAttributes();
    this.ordersAllowedOption = this.storeOrdersFlag;
  },

  methods: {
    ...mapMutations(['setDialog']),
    ...mapMutations('storePanel/transactions', [
      'setItem',
      'setCancelDialog',
      'setSuperUserConfirmationDialog',
      'setCustomerData',
      'setOrdersAllowed',
    ]),
    ...mapActions('storePanel/transactions', [
      'getItems',
      'changeStatus',
      'remove',
      'getTransactionsStatus',
      'getLast24Revenue',
      'getLastZ',
      'clearLastZUrl',
      'cancelTransaction',
      'getCustomerDetails',
      'getAttributes',
      'updateOrdersAllowed',
    ]),

    async ordersAllowedChanged() {
      await this.updateOrdersAllowed(this.ordersAllowedOption);
      this.ordersAllowedOption = this.ordersAllowed;
      if (this.store?.flags?.reward) {
        this.store.flags.reward.orders_allowed = this.ordersAllowedOption;
      }
    },

    async openCustomerDialog(id, mobile) {
      this.setCustomerData({});
      this.userAccountId = id;
      this.customerDialog = true;
      await this.getCustomerDetails({ id, mobile });
    },

    getPermission(path) {
      return (
        this.superUser ||
        ((this.permissions['*'] ||
          (this.permissions.transactions && eval('this.permissions.transactions.' + path))) &&
          (this.storePermissions['*'] ||
            (this.storePermissions.transactions &&
              eval('this.storePermissions.transactions.' + path))))
      );
    },
  },

  watch: {
    permission(val) {
      if (!val && !this.superUser) {
        this.$router.replace(`/${this.lang}/storePanel/forbidden-gateway`);
      }
    },

    storeOrdersFlag(val) {
      this.setOrdersAllowed(val);
      this.ordersAllowedOption = val;
    },

    notificationsTrigger(val) {
      this.getItems('?page=1');
    },

    $route(val) {
      if (!val.query.page) {
        this.$router.replace({
          query: {
            page: 1,
            ...this.$route.query,
          },
        });
      }

      this.getItems(this.query);
    },

    page(page) {
      this.$router.replace({ query: { ...this.$route.query, page } });
    },
  },

  beforeCreate() {
    if (!this.$store.state.storePanel.transactions) {
      this.$store.registerModule(['storePanel', 'transactions'], transactions);
    }

    if (!this.$route.query.page) {
      this.$router.replace({
        query: {
          page: 1,
          ...this.$route.query,
        },
      });
    }
  },
};
</script>
<style scoped>
.daily-revenue-box {
  border: 1px solid #e0e0e0;
}

.price-box {
  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
